import type { BranchDiffStatusData } from '.';
import type { SuperHubStore } from '@core/store';
import type { ReadBranchType } from '@readme/api-contracts/schemas/branch';
import type { ReviewReportFileSection } from '@readme/api-contracts/schemas/branch/reviewReport';
import type { CreateBranchType } from '@readme/api-contracts/schemas/version';
import type { BranchDiffSection } from '@readme/backend/iso';
import type { WritableDeep } from 'type-fest';

/**
 * RegEx to test if a diff category `title` looks like an API definition file
 * and NOT an `_order.yaml` file.
 * @example petstore.json
 * @example petstore.yaml
 * @example petstore.xml
 */
export const API_DEFINITION_EXT_REGEX = /^(?!_order\.yaml$).*\.(json|yaml|xml)$/i;

/**
 * RegEx to test if a diff page `slug` looks like an `_order.yaml` file.
 * @example order.yaml
 * @example _order.yaml
 * @example Category/Page/order.yaml
 * @example Category_Page_order.yaml
 * @example documentation-order.yaml
 */
export const ORDER_YAML_REGEX = /order\.yaml$/i;

/**
 * Lookup `superHubStore.branches.diffStatusMap` keys based on route section.
 */
export const routeToBranchDiffSection: Record<
  NonNullable<SuperHubStore['routeSection']>,
  BranchDiffSection | undefined
> = {
  api_definitions: 'apiDefinitions',
  changelog: undefined,
  custom_components: 'customComponents',
  discuss: undefined,
  docs: 'guides',
  home: undefined,
  page: 'customPages',
  recipes: 'recipes',
  reference: 'reference',
  reusable_content: 'customContent',
};

/**
 * Lookup `superHubStore.branches.diffStatusMap` keys based on
 * `superHubStore.branches.reviewReport.files` sections.
 */
export const reviewReportSectionToBranchDiffSection = {
  docs: 'guides',
  custom_pages: 'customPages',
  recipes: 'recipes',
  reference: 'reference',
} as const satisfies Record<ReviewReportFileSection, BranchDiffSection>;

/**
 * Lookup route sections based on `superHubStore.branches.diffStatusMap` keys.
 */
export const branchDiffSectionToRoute = {
  apiDefinitions: 'api_definitions',
  customComponents: 'custom_components',
  customContent: 'reusable_content',
  customPages: 'page',
  guides: 'docs',
  recipes: 'recipes',
  reference: 'reference',
} as const satisfies Record<BranchDiffSection, NonNullable<SuperHubStore['routeSection']>>;

/**
 * Represents an "ephemeral" branch that stores state during the create branch
 * flow.
 *
 * ⚠️ Note that this shape is different from the `CreateBranchType` shape from
 * the API. Specifically, the create branch flow needs to track the base version
 * it was launched from and the branch `name` suffix after the "_" delimiter
 * that gets input into the form from the user. Whereas the API type has a
 * single `name` which is the combined full branch name.
 *
 * @example
 * ```js
 * const ephemeralBranch: EphemeralBranch = {
 *   base: '1.0',
 *   name: 'my-branch'
 * }
 *
 * const createBranchPayload: CreateBranchType = {
 *   name: '1.0_my-branch'
 * }
 * ```
 */
export interface EphemeralBranch {
  base: ReadBranchType['data']['name'];
  name: CreateBranchType['name'];
  source: 'editor' | 'project-nav' | 'recipes-modal' | 'versions-panel';
}

/**
 * Returns an "empty" ephemeral branch object filled with default values used by
 * the create branch form.
 *
 * @see EphemeralBranch
 */
export function createEphemeralBranch<Keys extends EphemeralBranch>(keys: Partial<Keys>) {
  return {
    base: '',
    name: '',
    source: 'versions-panel',
    ...keys,
  } satisfies EphemeralBranch;
}

/**
 * Reduces the provided diff status SWR value and returns its response data
 * with a filtered `sidebar` field to only contain custom MDX components or
 * custom content categories based on the `type` passed in.
 */
export function reduceDiffStatusData<Data extends BranchDiffStatusData>(
  type: 'apiDefinition' | 'customComponent' | 'customContent' | 'reference',
  diffStatusData: Data | WritableDeep<Data>,
): Data['data'] | null {
  if (!diffStatusData.data) return null;

  switch (type) {
    case 'apiDefinition':
      return {
        sidebar:
          diffStatusData.data.sidebar?.filter(category => {
            return API_DEFINITION_EXT_REGEX.test(category.title);
          }) || [],
        status: diffStatusData.data.status,
      };

    case 'reference':
      return {
        sidebar:
          diffStatusData.data.sidebar?.filter(category => {
            return !API_DEFINITION_EXT_REGEX.test(category.title);
          }) || [],
        status: diffStatusData.data.status,
      };

    case 'customComponent':
      return {
        sidebar:
          diffStatusData.data.sidebar?.filter(category => {
            return category.title.endsWith('.mdx');
          }) || [],
        status: diffStatusData.data.status,
      };

    case 'customContent':
      return {
        sidebar:
          diffStatusData.data.sidebar?.filter(category => {
            return category.title === 'custom_blocks';
          }) || [],
        status: diffStatusData.data.status,
      };

    default:
      return diffStatusData.data;
  }
}
